import React, { useState, useEffect, useMemo } from 'react';
import FormSection from './FormSection';
import { listUnitsByProject } from '../services/unitsClient';
import { normalizeUnit } from '../utils/normalizeUnit';

const API_BASE = import.meta.env.VITE_API_BASE || 'https://u5aw5omfkd.execute-api.us-east-2.amazonaws.com/dev';

const PROJECT_OPTIONS = ["Aria", "Fusion", "SoMi Towns", "SoMi A", "SoMi B", "Vida", "Vida 2"];

const PROJECT_CANONICAL_MAP = {
    "Fusion": "Fusion",
    "Fusion - Hayward": "Fusion",
    "Fusion Hayward": "Fusion",
    "Fusion - Live-Work": "Fusion",
    "SoMi Haypark": "SoMi Towns",
    "SoMi HayPark": "SoMi Towns",
    "SoMi Towns": "SoMi Towns",
    "SoMi HayView": "SoMi A",
    "SoMi Hayview": "SoMi A",
    "SoMi Building A": "SoMi A",
    "SoMi A": "SoMi A",
    "SoMi Building B": "SoMi B",
    "SoMi Haypark Condos": "SoMi B",
    "SoMi B": "SoMi B",
};

function getCanonicalProjectId(value) {
    if (!value) return "";
    return PROJECT_CANONICAL_MAP[value] || value;
}

function getProjectAliasCandidates(value) {
    const canonical = getCanonicalProjectId(value);
    if (!canonical) return [];
    const candidates = new Set([canonical]);
    for (const [alias, target] of Object.entries(PROJECT_CANONICAL_MAP)) {
        if (target === canonical) {
            candidates.add(alias);
        }
    }
    return Array.from(candidates);
}

export default function CompletionProjectionsModal({ onClose }) {
    const [projectId, setProjectId] = useState("Aria");
    const [resolvedProjectId, setResolvedProjectId] = useState("");
    const [units, setUnits] = useState([]);

    const [selectedBuilding, setSelectedBuilding] = useState("");
    const [applyToAllUnits, setApplyToAllUnits] = useState(true);
    const [selectedUnits, setSelectedUnits] = useState([]);
    const [completionDate, setCompletionDate] = useState("");
    const [loading, setLoading] = useState(false);
    const [saveMessage, setSaveMessage] = useState("");
    const [saveError, setSaveError] = useState("");

    const activeProjectId = useMemo(() => getCanonicalProjectId(projectId), [projectId]);

    function prioritizeCandidates(list) {
        if (!resolvedProjectId || !list.includes(resolvedProjectId)) return list;
        const ordered = [resolvedProjectId, ...list];
        return Array.from(new Set(ordered));
    }

    // Load units for project
    useEffect(() => {
        let abort = false;
        async function run() {
            const canonical = activeProjectId || projectId;
            if (!canonical) return;
            const candidates = prioritizeCandidates(getProjectAliasCandidates(canonical));
            let loaded = false;
            setLoading(true);
            for (const candidate of candidates) {
                try {
                    const rsp = await listUnitsByProject({ project_id: candidate });
                    const items = (rsp.items || rsp || []).map(normalizeUnit);
                    if (abort) return;
                    if (items.length > 0) {
                        setResolvedProjectId(candidate);
                        setUnits(items);
                        loaded = true;
                        break;
                    }
                } catch (e) {
                    console.error("units load error", candidate, e);
                }
            }
            if (!loaded && !abort) {
                setUnits([]);
            }
            if (!abort) {
                setLoading(false);
            }
        }
        run();
        return () => { abort = true; };
    }, [activeProjectId, projectId]);

    const buildings = useMemo(() => Array.from(new Set(units.map(u => u.building_id).filter(Boolean))).sort(), [units]);

    const availableUnits = useMemo(() => {
        if (!selectedBuilding) return [];
        return units
            .filter(u => u.building_id === selectedBuilding)
            .map(u => String(u.unit_number))
            .sort((a, b) => {
                const numA = Number(a);
                const numB = Number(b);
                if (Number.isFinite(numA) && Number.isFinite(numB)) return numA - numB;
                return a.localeCompare(b, undefined, { numeric: true });
            });
    }, [units, selectedBuilding]);

    useEffect(() => {
        setResolvedProjectId("");
        setSelectedBuilding("");
        setApplyToAllUnits(true);
        setSelectedUnits([]);
        setSaveMessage("");
        setSaveError("");
    }, [projectId]);

    useEffect(() => {
        setApplyToAllUnits(true);
        setSelectedUnits([]);
        setSaveMessage("");
        setSaveError("");
    }, [selectedBuilding]);

    const toggleUnitSelection = (unitNumber) => {
        setApplyToAllUnits(false);
        setSaveMessage("");
        setSaveError("");
        setSelectedUnits((current) => (
            current.includes(unitNumber)
                ? current.filter((unit) => unit !== unitNumber)
                : [...current, unitNumber]
        ));
    };

    const handleSelectAllUnits = () => {
        setApplyToAllUnits(true);
        setSelectedUnits([]);
        setSaveMessage("");
        setSaveError("");
    };

    const handleSave = async () => {
        if (!projectId || !selectedBuilding || !completionDate) {
            setSaveError("Please ensure Project, Building, and Completion Date are filled.");
            return;
        }

        if (!applyToAllUnits && selectedUnits.length === 0) {
            setSaveError("Select at least one unit, or choose All Units in Building.");
            return;
        }

        setLoading(true);
        setSaveMessage("");
        setSaveError("");
        try {
            const targets = applyToAllUnits ? [null] : selectedUnits;
            for (const unitNumber of targets) {
                const payload = {
                    project_id: resolvedProjectId || projectId,
                    building_id: selectedBuilding,
                    unit_number: unitNumber,
                    ops_projected_coe: completionDate
                };

                const response = await fetch(`${API_BASE}/projections`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    throw new Error(`Failed to save: ${response.status} ${response.statusText}`);
                }
            }

            const scopeLabel = applyToAllUnits
                ? `all ${availableUnits.length} units in ${selectedBuilding}`
                : `${selectedUnits.length} unit${selectedUnits.length === 1 ? "" : "s"} in ${selectedBuilding}`;
            setSaveMessage(`Saved projection for ${scopeLabel}.`);
        } catch (error) {
            console.error("Save error:", error);
            setSaveError(error.message || "Failed to save projection.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={modalOverlayStyle}>
            <div style={modalContentStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.5rem' }}>
                    <h2 style={{ fontSize: '1.25rem', margin: 0 }}>
                        Sales / Comp. Projections
                    </h2>
                    <button onClick={onClose} style={closeButtonStyle}>✕</button>
                </div>

                <FormSection title="Target Selection">
                    <div className="grid-2">
                        <label>
                            Project
                            <select value={projectId} onChange={e => setProjectId(e.target.value)}>
                                {PROJECT_OPTIONS.map(p => (
                                    <option key={p} value={p}>{p}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            Building
                            <select
                                value={selectedBuilding}
                                onChange={e => setSelectedBuilding(e.target.value)}
                                disabled={loading || buildings.length === 0}
                            >
                                <option value="">-- Select Building --</option>
                                {buildings.map(b => <option key={b} value={b}>{b}</option>)}
                            </select>
                        </label>
                    </div>
                    <div style={{ marginTop: '0.75rem' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.45rem' }}>Units</div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                            <button
                                type="button"
                                className="btn"
                                onClick={handleSelectAllUnits}
                                disabled={!selectedBuilding || availableUnits.length === 0}
                                style={applyToAllUnits ? selectedToggleStyle : toggleStyle}
                            >
                                All Units in Building
                            </button>
                            {!applyToAllUnits && (
                                <span style={{ fontSize: '0.85rem', color: '#4b5563' }}>
                                    {selectedUnits.length} selected
                                </span>
                            )}
                        </div>
                        <div style={unitGridStyle}>
                            {availableUnits.length === 0 ? (
                                <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                                    Select a building to choose units.
                                </div>
                            ) : (
                                availableUnits.map(unit => {
                                    const isSelected = !applyToAllUnits && selectedUnits.includes(unit);
                                    return (
                                        <button
                                            key={unit}
                                            type="button"
                                            onClick={() => toggleUnitSelection(unit)}
                                            style={isSelected ? selectedUnitChipStyle : unitChipStyle}
                                        >
                                            Unit {unit}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </FormSection>

                <FormSection title="Projection Data" style={{ marginTop: '1rem' }}>
                    <div className="grid-2">
                        <label>
                            Projected Date
                            <input
                                type="date"
                                value={completionDate}
                                onChange={e => setCompletionDate(e.target.value)}
                            />
                        </label>
                    </div>
                </FormSection>

                <div className="actions" style={{ marginTop: '1.5rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                    {(saveMessage || saveError) && (
                        <div style={{ width: '100%', marginBottom: '0.75rem', color: saveError ? '#b91c1c' : '#166534', fontSize: '0.9rem' }}>
                            {saveError || saveMessage}
                        </div>
                    )}
                    <button className="btn" onClick={onClose}>Cancel</button>
                    <button
                        className="btn primary"
                        onClick={handleSave}
                        disabled={loading || !selectedBuilding || !completionDate || (!applyToAllUnits && selectedUnits.length === 0)}
                    >
                        Save Projection
                    </button>
                </div>
            </div>
        </div>
    );
}

const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '1rem'
};

const modalContentStyle = {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '1.5rem',
    width: '100%',
    maxWidth: '600px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    maxHeight: '90vh',
    overflowY: 'auto'
};

const closeButtonStyle = {
    background: 'none',
    border: 'none',
    fontSize: '1.25rem',
    cursor: 'pointer',
    color: '#6b7280',
    padding: '0.25rem'
};

const unitGridStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    minHeight: '2.25rem'
};

const toggleStyle = {
    border: '1px solid #d1d5db',
    backgroundColor: '#ffffff',
    color: '#111827'
};

const selectedToggleStyle = {
    ...toggleStyle,
    borderColor: '#2563eb',
    backgroundColor: '#dbeafe',
    color: '#1d4ed8'
};

const unitChipStyle = {
    border: '1px solid #d1d5db',
    borderRadius: '999px',
    backgroundColor: '#ffffff',
    color: '#111827',
    padding: '0.4rem 0.75rem',
    cursor: 'pointer',
    fontSize: '0.9rem'
};

const selectedUnitChipStyle = {
    ...unitChipStyle,
    borderColor: '#2563eb',
    backgroundColor: '#dbeafe',
    color: '#1d4ed8'
};
